// Puck Header Bundle - Webpack entry point for header editor
import React from "react";
import ReactDOM from "react-dom/client";
import { Puck, Render } from "@puckeditor/core";
import "@puckeditor/core/puck.css";

// TipTap imports for rich text editing
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";

/*
 * Import utilities and fields from existing puckBundle
 * Note: These will need to be accessible from the existing bundle or duplicated
 */
import {
  AccessibilityWarning,
  applyAuthToButtons,
  isButtonVisible,
  getUserAuthState,
  getProfileUrl
} from "../HubContentTab/utils/index.js";
import {
  ColorPickerField,
  MediaField,
  VideoAltTextField,
  videoA11yProps
} from "../HubContentTab/fields/index.js";
import {
  createWelcomeBlock,
  createContainerBlock
} from "../HubContentTab/components/index.js";

// Local references for backward compatibility
const MediaFieldLocal = MediaField;
const ColorPickerFieldLocal = ColorPickerField;

// Constants for magic numbers
const OPACITY_075 = 0.75;
const OPACITY_05 = 0.5;
const FONT_SIZE_PX_12 = 12;
const FONT_SIZE_PX_16 = 16;
const BORDER_RADIUS_4_PX = 4;
const PERCENT_100 = 100;
const MAX_LENGTH_200 = 200;
const MIN_LENGTH_20 = 20;
const MAX_COLUMNS = 4;
const DEFAULT_COLUMN_COUNT = 2;
// Hex color parsing offsets
const HEX_COLOR_START_INDEX = 0;
const HEX_COLOR_R_END_INDEX = 2;
const HEX_COLOR_G_START_INDEX = 2;
const HEX_COLOR_B_START_INDEX = 4;
const HEX_RADIX = 16;

// Video field component with URL requirements
function VideoField({ field, onChange, value }) {
  return React.createElement(
    "div",
    { className: "puck-field" },
    React.createElement(
      "label",
      { className: "puck-field-label" },
      field.label || "Video URL"
    ),
    React.createElement("input", {
      type: "text",
      value: value || "",
      onChange: (e) => onChange(e.target.value),
      className: "form-control",
      placeholder: "https://example.com/video.mp4",
      style: { marginBottom: "0.5rem" }
    }),
    React.createElement(
      "div",
      {
        className: "alert alert-info",
        style: {
          fontSize: "0.875rem",
          padding: "0.75rem",
          marginTop: "0.5rem",
          marginBottom: 0
        }
      },
      React.createElement("strong", {}, "Video URL Requirements:"),
      React.createElement(
        "ul",
        {
          style: {
            marginBottom: 0,
            paddingLeft: "1.25rem",
            marginTop: "0.5rem"
          }
        },
        React.createElement(
          "li",
          {},
          "Must be a direct link to a video file (MP4, WebM, or OGG format)"
        ),
        React.createElement(
          "li",
          {},
          "Video should be optimized for web (recommended: H.264 codec, max 10MB)"
        ),
        React.createElement(
          "li",
          {},
          "URL must be publicly accessible (no authentication required)"
        ),
        React.createElement("li", {}, "For best performance, use HTTPS URLs"),
        React.createElement(
          "li",
          {},
          "Recommended: MP4 format with H.264 codec for maximum browser compatibility"
        )
      )
    )
  );
}

// Title color field with accessibility warnings
function TitleColorFieldWithWarning({ field, onChange, value, ...props }) {
  const [componentData, setComponentData] = React.useState(null);

  React.useEffect(function () {
    function checkForComponentData() {
      if (window.getPuckHeaderEditorData) {
        const data = window.getPuckHeaderEditorData();
        if (data && data.content) {
          const heroComponent = data.content.find((c) => c.type === "Hero");
          if (heroComponent && heroComponent.props) {
            setComponentData(heroComponent.props);
            return;
          }
        }
      }
      setComponentData(null);
    }

    checkForComponentData();
    function handleDataChange() {
      checkForComponentData();
    }

    document.addEventListener("puck:dataChange", handleDataChange);
    return function () {
      document.removeEventListener("puck:dataChange", handleDataChange);
    };
  }, []);

  const gradient = componentData?.gradient || {};
  const image = componentData?.image || {};
  const textOverlay = componentData?.textOverlay || {};

  function ensureColorString(color) {
    if (!color) {
      return null;
    }
    if (typeof color === "string") {
      return color;
    }
    if (
      typeof color === "object" &&
      color.r != null &&
      color.g != null &&
      color.b != null
    ) {
      const a = color.a != null ? color.a : 1;
      return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(
        color.b
      )}, ${a})`;
    }
    return String(color);
  }

  const backgroundColor =
    gradient && gradient.enabled && gradient.color
      ? ensureColorString(gradient.color)
      : image && image.mode === "bg" && image.url
      ? "rgba(0, 0, 0, 0.5)"
      : "#ffffff";

  const overlayEnabled = textOverlay && textOverlay.enabled;
  const overlayColor =
    overlayEnabled && textOverlay.backgroundColor
      ? ensureColorString(textOverlay.backgroundColor)
      : null;

  const textColorString = ensureColorString(value);
  const titleTypography = componentData?.titleTypography || {
    fontSize: "40",
    fontWeight: "700"
  };

  return React.createElement(
    "div",
    {},
    React.createElement(ColorPickerField, { field, onChange, value, ...props }),
    React.createElement(AccessibilityWarning, {
      textColor: textColorString,
      backgroundColor: backgroundColor,
      overlayEnabled: overlayEnabled,
      overlayColor: overlayColor,
      fontSize: parseInt(titleTypography.fontSize || "40"),
      fontWeight: parseInt(titleTypography.fontWeight || "700"),
      fieldName: "title"
    })
  );
}

// Description color field with accessibility warnings
function DescriptionColorFieldWithWarning({
  field,
  onChange,
  value,
  ...props
}) {
  const [componentData, setComponentData] = React.useState(null);

  React.useEffect(function () {
    function checkForComponentData() {
      if (window.getPuckHeaderEditorData) {
        const data = window.getPuckHeaderEditorData();
        if (data && data.content) {
          const heroComponent = data.content.find((c) => c.type === "Hero");
          if (heroComponent && heroComponent.props) {
            setComponentData(heroComponent.props);
            return;
          }
        }
      }
      setComponentData(null);
    }

    checkForComponentData();
    function handleDataChange() {
      checkForComponentData();
    }

    document.addEventListener("puck:dataChange", handleDataChange);
    return function () {
      document.removeEventListener("puck:dataChange", handleDataChange);
    };
  }, []);

  const gradient = componentData?.gradient || {};
  const image = componentData?.image || {};
  const textOverlay = componentData?.textOverlay || {};

  function ensureColorString(color) {
    if (!color) {
      return null;
    }
    if (typeof color === "string") {
      return color;
    }
    if (
      typeof color === "object" &&
      color.r != null &&
      color.g != null &&
      color.b != null
    ) {
      const a = color.a != null ? color.a : 1;
      return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(
        color.b
      )}, ${a})`;
    }
    return String(color);
  }

  const backgroundColor =
    gradient && gradient.enabled && gradient.color
      ? ensureColorString(gradient.color)
      : image && image.mode === "bg" && image.url
      ? "rgba(0, 0, 0, 0.5)"
      : "#ffffff";

  const overlayEnabled = textOverlay && textOverlay.enabled;
  const overlayColor =
    overlayEnabled && textOverlay.backgroundColor
      ? ensureColorString(textOverlay.backgroundColor)
      : null;

  const textColorString = ensureColorString(value);
  const descTypography = componentData?.descriptionTypography || {
    fontSize: "18",
    fontWeight: "400"
  };

  return React.createElement(
    "div",
    {},
    React.createElement(ColorPickerField, { field, onChange, value, ...props }),
    React.createElement(AccessibilityWarning, {
      textColor: textColorString,
      backgroundColor: backgroundColor,
      overlayEnabled: overlayEnabled,
      overlayColor: overlayColor,
      fontSize: parseInt(descTypography.fontSize || "18"),
      fontWeight: parseInt(descTypography.fontWeight || "400"),
      fieldName: "description"
    })
  );
}

// Rich Text Field Component (reused from puckBundle)
function RichTextField({ onChange, value }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false
      }),
      Highlight,
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      Underline,
      TextStyle,
      Color
    ],
    content: value || "<p>Enter your text here</p>",
    onUpdate: ({ editor: editorInstance }) => {
      onChange(editorInstance.getHTML());
    }
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return React.createElement(
    "div",
    { className: "rich-text-editor" },
    React.createElement(
      "div",
      {
        className: "rich-text-toolbar",
        style: {
          border: "1px solid #ddd",
          borderBottom: "none",
          padding: "0.5rem",
          backgroundColor: "#f8f9fa",
          display: "flex",
          gap: "0.25rem",
          flexWrap: "wrap"
        }
      },
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleBold().run(),
          className: editor.isActive("bold") ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("bold") ? "#007bff" : "#fff",
            color: editor.isActive("bold") ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "B"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleItalic().run(),
          className: editor.isActive("italic") ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("italic") ? "#007bff" : "#fff",
            color: editor.isActive("italic") ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "I"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () => editor.chain().focus().toggleUnderline().run(),
          className: editor.isActive("underline") ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("underline") ? "#007bff" : "#fff",
            color: editor.isActive("underline") ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "U"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
          className: editor.isActive("heading", { level: 1 }) ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("heading", { level: 1 })
              ? "#007bff"
              : "#fff",
            color: editor.isActive("heading", { level: 1 }) ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "H1"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
          className: editor.isActive("heading", { level: 2 }) ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("heading", { level: 2 })
              ? "#007bff"
              : "#fff",
            color: editor.isActive("heading", { level: 2 }) ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "H2"
      ),
      React.createElement(
        "button",
        {
          type: "button",
          onClick: () =>
            editor
              .chain()
              .focus()
              .setLink({ href: prompt("Enter URL:") || "" })
              .run(),
          className: editor.isActive("link") ? "active" : "",
          style: {
            padding: "0.25rem 0.5rem",
            border: "1px solid #ccc",
            backgroundColor: editor.isActive("link") ? "#007bff" : "#fff",
            color: editor.isActive("link") ? "#fff" : "#000",
            cursor: "pointer"
          }
        },
        "Link"
      )
    ),
    React.createElement(EditorContent, {
      editor: editor,
      style: {
        border: "1px solid #ddd",
        padding: "0.75rem",
        minHeight: "150px",
        backgroundColor: "#fff"
      }
    })
  );
}

// Translate Widget Component - Visual representation only, actual translation handled externally
const TranslateWidget = {
  label: "Translation Toggle",
  fields: {
    label: {
      type: "text",
      label: "Button Label",
      placeholder: "Translate"
    },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Right", value: "right" },
        { label: "Left", value: "left" },
        { label: "Center", value: "center" }
      ]
    }
  },
  defaultProps: {
    label: "Translate",
    position: "right"
  },
  render: ({ label, position }) => {
    return React.createElement(
      "div",
      {
        className: "translate-widget-placeholder",
        style: {
          display: "inline-block",
          padding: "0.5rem 1rem",
          border: "2px dashed #ccc",
          borderRadius: `${BORDER_RADIUS_4_PX}px`,
          backgroundColor: "#f8f9fa",
          color: "#6c757d",
          fontSize: "0.875rem",
          textAlign: position
        }
      },
      `[${label}] - Translation handled externally`
    );
  }
};

// Header-specific Puck configuration
const puckHeaderConfig = {
  root: {
    title: "Hero Section",
    fields: {
      hubId: {
        type: "custom",
        label: "# Hub ID (auto-detected)",
        render: ({ field, value, onChange }) => {
          function getAutoHubId() {
            if (window.modelData && window.modelData.hubId) {
              const id = window.modelData.hubId;
              if (id && id > 0) {
                return id;
              }
            }
            if (window.modelData && window.modelData.hub) {
              const hub = window.modelData.hub;
              if (hub.id != null && hub.id > 0) {
                return hub.id;
              }
              if (hub.Id != null && hub.Id > 0) {
                return hub.Id;
              }
            }
            return value || 0;
          }

          const autoHubId = getAutoHubId();
          const hasUpdatedRef = React.useRef(false);

          React.useEffect(() => {
            if (
              !hasUpdatedRef.current &&
              autoHubId > 0 &&
              autoHubId !== value
            ) {
              onChange(autoHubId);
              hasUpdatedRef.current = true;
            }
          }, [autoHubId, value, onChange]);

          return React.createElement(
            "div",
            { className: "puck-field" },
            React.createElement(
              "label",
              { className: "puck-field-label" },
              field.label
            ),
            React.createElement("input", {
              type: "text",
              value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
              className: "form-control",
              readOnly: true,
              disabled: true,
              style: { backgroundColor: "#e9ecef", cursor: "not-allowed" }
            }),
            autoHubId === 0 &&
              React.createElement(
                "small",
                {
                  className: "form-text text-warning"
                },
                "Hub ID will be auto-detected when available"
              )
          );
        }
      }
    }
  },
  categories: {
    layout: {
      title: "LAYOUT",
      components: ["Columns", "VerticalSpace", "Container"]
    },
    typography: {
      title: "TYPOGRAPHY",
      components: ["HeadingBlock", "TextBlock"]
    },
    actions: {
      title: "ACTIONS",
      components: ["ButtonBlock", "ButtonGroup"]
    },
    media: {
      title: "MEDIA",
      components: ["ImageBlock", "Icon"]
    },
    content: {
      title: "CONTENT",
      components: ["Hero", "Stats", "Logos", "WelcomeBlock"]
    },
    ui: {
      title: "UI ELEMENTS",
      components: ["Divider", "TranslateWidget"]
    }
  },
  components: {
    HeadingBlock: {
      label: "Heading",
      fields: {
        text: {
          type: "textarea",
          label: "Text",
          contentEditable: true
        },
        level: {
          type: "select",
          label: "Level",
          options: [
            { label: "H1", value: "h1" },
            { label: "H2", value: "h2" },
            { label: "H3", value: "h3" },
            { label: "H4", value: "h4" }
          ]
        },
        align: {
          type: "select",
          label: "Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        }
      },
      defaultProps: { text: "Heading", level: "h2", align: "left" },
      render: ({ text, level, align }) => {
        const Level = level || "h2";
        return React.createElement(
          Level,
          {
            style: { textAlign: align }
          },
          text
        );
      }
    },
    TextBlock: {
      label: "Text",
      fields: {
        content: {
          type: "custom",
          label: "Content",
          render: RichTextField
        },
        size: {
          type: "select",
          label: "Size",
          options: [
            { label: "Small", value: "small" },
            { label: "Normal", value: "normal" },
            { label: "Large", value: "large" }
          ]
        }
      },
      defaultProps: { content: "<p>Enter your text here</p>", size: "normal" },
      render: ({ content, size }) => {
        const fontSize =
          size === "small" ? "0.875rem" : size === "large" ? "1.25rem" : "1rem";
        return React.createElement("div", {
          className: "text-block-content",
          style: { fontSize: fontSize },
          dangerouslySetInnerHTML: { __html: content || "" }
        });
      }
    },
    ButtonBlock: {
      label: "Button",
      fields: {
        text: {
          type: "text",
          label: "Button Text"
        },
        url: {
          type: "text",
          label: "URL"
        },
        style: {
          type: "select",
          label: "Style",
          options: [
            { label: "Primary", value: "primary" },
            { label: "Secondary", value: "secondary" },
            { label: "Outline", value: "outline" }
          ]
        },
        visibility: {
          type: "select",
          label: "Visibility (editor preview shows signed-out state)",
          options: [
            { label: "Always show", value: "always" },
            { label: "Only when signed out", value: "signedOut" },
            { label: "Only when signed in", value: "signedIn" }
          ]
        }
      },
      defaultProps: {
        text: "Click Me",
        url: "#",
        style: "primary",
        visibility: "always"
      },
      render: ({ text, url, style, visibility }) => {
        const { isLoggedIn, displayName } = getUserAuthState();
        const button = { url, visibility };
        const visible = isButtonVisible(button, isLoggedIn);
        let buttonText = text;
        let buttonUrl = url;

        if (!visible) {
          // A hidden signed-out button (e.g. "Sign in") becomes the visitor's
          // name linking to their profile; signed-in-only buttons just hide.
          if (!isLoggedIn || visibility === "signedIn") {
            return null;
          }
          buttonText = displayName || "My Profile";
          buttonUrl = getProfileUrl();
        }

        const styleMap = {
          primary: { backgroundColor: "#007bff", color: "#fff" },
          secondary: { backgroundColor: "#6c757d", color: "#fff" },
          outline: {
            backgroundColor: "transparent",
            color: "#007bff",
            border: "1px solid #007bff"
          }
        };
        return React.createElement(
          "a",
          {
            href: buttonUrl,
            className: "btn",
            style: {
              ...(styleMap[style] || styleMap.primary),
              padding: "0.5rem 1rem",
              borderRadius: `${BORDER_RADIUS_4_PX}px`,
              textDecoration: "none",
              display: "inline-block"
            }
          },
          buttonText
        );
      }
    },
    ImageBlock: {
      label: "Image",
      fields: {
        src: {
          type: "custom",
          label: "Image",
          render: MediaField
        },
        alt: {
          type: "text",
          label: "Alt Text"
        },
        width: {
          type: "text",
          label: "Width (e.g., 100px or 50%)"
        }
      },
      defaultProps: {
        src: "",
        alt: "",
        width: "100%"
      },
      render: ({ src, alt, width }) => {
        if (!src) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "2rem",
                backgroundColor: "#f8f9fa",
                border: "2px dashed #dee2e6",
                textAlign: "center",
                color: "#6c757d"
              }
            },
            "No image selected"
          );
        }
        return React.createElement("img", {
          src: src,
          alt: alt || "",
          style: { width: width || "100%", height: "auto" }
        });
      }
    },
    Hero: {
      label: "Hero",
      fields: {
        title: {
          type: "text",
          label: "Title"
        },
        titleTypography: {
          type: "object",
          label: "Title Typography",
          objectFields: {
            fontSize: {
              type: "select",
              label: "Font Size",
              options: [
                { label: "Small (1.5rem / 24px)", value: "24" },
                { label: "Medium (2rem / 32px)", value: "32" },
                { label: "Large (2.5rem / 40px)", value: "40" },
                { label: "Extra Large (3rem / 48px)", value: "48" },
                { label: "XXL (3.5rem / 56px)", value: "56" },
                { label: "XXXL (4rem / 64px)", value: "64" }
              ]
            },
            fontWeight: {
              type: "select",
              label: "Font Weight",
              options: [
                { label: "Light (300)", value: "300" },
                { label: "Normal (400)", value: "400" },
                { label: "Medium (500)", value: "500" },
                { label: "Semi-Bold (600)", value: "600" },
                { label: "Bold (700)", value: "700" },
                { label: "Extra Bold (800)", value: "800" }
              ]
            },
            titleColor: {
              type: "custom",
              label: "Title Color",
              render: ({ field, onChange, value, ...props }) => {
                return React.createElement(TitleColorFieldWithWarning, {
                  field,
                  onChange,
                  value,
                  ...props
                });
              }
            }
          }
        },
        description: {
          type: "textarea",
          label: "Description"
        },
        descriptionTypography: {
          type: "object",
          label: "Description Typography",
          objectFields: {
            fontSize: {
              type: "select",
              label: "Font Size",
              options: [
                { label: "Small (0.875rem / 14px)", value: "14" },
                { label: "Normal (1rem / 16px)", value: "16" },
                { label: "Large (1.125rem / 18px)", value: "18" },
                { label: "XL (1.25rem / 20px)", value: "20" },
                { label: "XXL (1.5rem / 24px)", value: "24" }
              ]
            },
            fontWeight: {
              type: "select",
              label: "Font Weight",
              options: [
                { label: "Light (300)", value: "300" },
                { label: "Normal (400)", value: "400" },
                { label: "Medium (500)", value: "500" },
                { label: "Semi-Bold (600)", value: "600" },
                { label: "Bold (700)", value: "700" }
              ]
            },
            descriptionColor: {
              type: "custom",
              label: "Description Color",
              render: ({ field, onChange, value, ...props }) => {
                return React.createElement(DescriptionColorFieldWithWarning, {
                  field,
                  onChange,
                  value,
                  ...props
                });
              }
            }
          }
        },
        buttons: {
          type: "array",
          label: "Buttons",
          getItemSummary: (item, index) => {
            if (item && item.label && item.label.trim()) {
              return item.label.trim();
            }
            return `Button ${index + 1}`;
          },
          arrayFields: {
            label: { type: "text", label: "Button Text" },
            href: { type: "text", label: "Link URL" },
            variant: {
              type: "select",
              label: "Button Style",
              options: [
                { label: "Primary", value: "primary" },
                { label: "Secondary", value: "secondary" },
                { label: "Success", value: "success" },
                { label: "Danger", value: "danger" },
                { label: "Warning", value: "warning" },
                { label: "Info", value: "info" },
                { label: "Light", value: "light" },
                { label: "Dark", value: "dark" },
                { label: "Outline Primary", value: "outline-primary" },
                { label: "Outline Secondary", value: "outline-secondary" },
                { label: "Outline Light", value: "outline-light" }
              ]
            },
            visibility: {
              type: "select",
              label: "Visibility (editor preview shows signed-out state)",
              options: [
                { label: "Always show", value: "always" },
                { label: "Only when signed out", value: "signedOut" },
                { label: "Only when signed in", value: "signedIn" }
              ]
            }
          },
          defaultItemProps: {
            label: "Learn More",
            href: "#",
            variant: "primary",
            visibility: "always"
          }
        },
        buttonAlign: {
          type: "select",
          label: "Button Alignment",
          options: [
            { label: "Left", value: "left" },
            { label: "Center", value: "center" },
            { label: "Right", value: "right" }
          ]
        },
        backgroundType: {
          type: "select",
          label: "Background Type",
          options: [
            { label: "None", value: "none" },
            { label: "Image", value: "image" },
            { label: "Video", value: "video" }
          ]
        },
        image: {
          type: "custom",
          label: "Image",
          render: ({ field, value, onChange }) => {
            function getBackgroundType() {
              if (window.getPuckHeaderEditorData) {
                const data = window.getPuckHeaderEditorData();
                if (data && data.content) {
                  const heroComponent = data.content.find(
                    (c) => c.type === "Hero"
                  );
                  if (
                    heroComponent &&
                    heroComponent.props &&
                    heroComponent.props.backgroundType
                  ) {
                    return heroComponent.props.backgroundType;
                  }
                }
              }
              return "none";
            }

            const [backgroundType, setBackgroundType] = React.useState(
              getBackgroundType()
            );

            React.useEffect(() => {
              function checkBackgroundType() {
                const currentType = getBackgroundType();
                if (currentType !== backgroundType) {
                  setBackgroundType(currentType);
                }
              }

              checkBackgroundType();
              document.addEventListener("puck:dataChange", checkBackgroundType);
              return () => {
                document.removeEventListener(
                  "puck:dataChange",
                  checkBackgroundType
                );
              };
            }, [backgroundType]);

            if (backgroundType !== "image") {
              return null;
            }

            return React.createElement(
              "div",
              { className: "puck-field-group" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label || "Image"
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "URL"
                ),
                React.createElement(MediaFieldLocal, {
                  field: { label: "URL" },
                  value: value?.url || "",
                  onChange: (url) => onChange({ ...value, url })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Mode"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  ["inline", "bg", "custom"].map((mode) =>
                    React.createElement(
                      "button",
                      {
                        key: mode,
                        type: "button",
                        className: `btn btn-sm ${
                          (value?.mode || "inline") === mode
                            ? "btn-primary"
                            : "btn-outline-secondary"
                        }`,
                        onClick: () => onChange({ ...value, mode })
                      },
                      mode === "inline"
                        ? "Inline"
                        : mode === "bg"
                        ? "Background"
                        : "Custom"
                    )
                  )
                )
              )
            );
          }
        },
        video: {
          type: "custom",
          label: "Video Background",
          render: ({ field, value, onChange }) => {
            function getBackgroundType() {
              if (window.getPuckHeaderEditorData) {
                const data = window.getPuckHeaderEditorData();
                if (data && data.content) {
                  const heroComponent = data.content.find(
                    (c) => c.type === "Hero"
                  );
                  if (
                    heroComponent &&
                    heroComponent.props &&
                    heroComponent.props.backgroundType
                  ) {
                    return heroComponent.props.backgroundType;
                  }
                }
              }
              return "none";
            }

            const [backgroundType, setBackgroundType] = React.useState(
              getBackgroundType()
            );

            React.useEffect(() => {
              function checkBackgroundType() {
                const currentType = getBackgroundType();
                if (currentType !== backgroundType) {
                  setBackgroundType(currentType);
                }
              }

              checkBackgroundType();
              document.addEventListener("puck:dataChange", checkBackgroundType);
              return () => {
                document.removeEventListener(
                  "puck:dataChange",
                  checkBackgroundType
                );
              };
            }, [backgroundType]);

            if (backgroundType !== "video") {
              return null;
            }

            return React.createElement(
              "div",
              { className: "puck-field-group" },
              React.createElement(
                "label",
                { className: "puck-field-label" },
                field.label || "Video Background"
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Video URL"
                ),
                React.createElement(VideoField, {
                  field: { label: "Video URL" },
                  value: value?.url || "",
                  onChange: (url) => onChange({ ...value, url })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Poster Image (fallback/thumbnail)"
                ),
                React.createElement(MediaFieldLocal, {
                  field: { label: "Poster Image" },
                  value: value?.poster || "",
                  onChange: (poster) => onChange({ ...value, poster })
                })
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Autoplay"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.autoplay !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, autoplay: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.autoplay === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, autoplay: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Loop"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.loop !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, loop: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.loop === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, loop: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(
                "div",
                { className: "puck-field" },
                React.createElement(
                  "label",
                  { className: "puck-field-label" },
                  "Muted"
                ),
                React.createElement(
                  "div",
                  { className: "btn-group", role: "group" },
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.muted !== false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, muted: true })
                    },
                    "Yes"
                  ),
                  React.createElement(
                    "button",
                    {
                      type: "button",
                      className: `btn btn-sm ${
                        value?.muted === false
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`,
                      onClick: () => onChange({ ...value, muted: false })
                    },
                    "No"
                  )
                )
              ),
              React.createElement(VideoAltTextField, { value, onChange })
            );
          }
        },
        padding: {
          type: "text",
          label: "Padding"
        },
        gradient: {
          type: "object",
          label: "Gradient Overlay",
          objectFields: {
            enabled: {
              type: "radio",
              label: "Enable Gradient",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            color: {
              type: "custom",
              label: "Overlay Color",
              render: (props) =>
                React.createElement(ColorPickerField, {
                  ...props,
                  showAlpha: true
                })
            },
            type: {
              type: "select",
              label: "Overlay Type",
              options: [
                { label: "Gradient", value: "gradient" },
                { label: "No gradient - solid", value: "solid" }
              ]
            },
            direction: {
              type: "select",
              label: "Gradient Direction",
              options: [
                { label: "To Left", value: "to left" },
                { label: "To Right", value: "to right" },
                { label: "To Top", value: "to top" },
                { label: "To Bottom", value: "to bottom" },
                { label: "To Top Left", value: "to top left" },
                { label: "To Top Right", value: "to top right" },
                { label: "To Bottom Left", value: "to bottom left" },
                { label: "To Bottom Right", value: "to bottom right" }
              ]
            }
          }
        },
        textOverlay: {
          type: "object",
          label: "Text Overlay Box",
          objectFields: {
            enabled: {
              type: "radio",
              label: "Enable Text Overlay Box",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            backgroundColor: {
              type: "custom",
              label: "Background Color",
              render: (props) =>
                React.createElement(ColorPickerField, {
                  ...props,
                  showAlpha: true
                })
            },
            borderRadius: {
              type: "select",
              label: "Border Radius",
              options: [
                { label: "None (0px)", value: "0" },
                { label: "Small (4px)", value: "4" },
                { label: "Medium (8px)", value: "8" },
                { label: "Large (12px)", value: "12" },
                { label: "XL (16px)", value: "16" },
                { label: "XXL (24px)", value: "24" },
                { label: "Round (50px)", value: "50" }
              ]
            },
            shadow: {
              type: "radio",
              label: "Shadow",
              options: [
                { label: "On", value: true },
                { label: "Off", value: false }
              ]
            },
            padding: {
              type: "select",
              label: "Padding",
              options: [
                { label: "Small (1rem)", value: "1rem" },
                { label: "Medium (1.5rem)", value: "1.5rem" },
                { label: "Large (2rem)", value: "2rem" },
                { label: "XL (2.5rem)", value: "2.5rem" },
                { label: "XXL (3rem)", value: "3rem" }
              ]
            },
            overlayAlign: {
              type: "select",
              label: "Overlay Box Alignment",
              options: [
                { label: "Left", value: "left" },
                { label: "Center", value: "center" },
                { label: "Right", value: "right" }
              ]
            },
            overlayWidth: {
              type: "select",
              label: "Overlay Box Width (Desktop)",
              options: [
                { label: "Full Width", value: "full" },
                { label: "Narrow (400px)", value: "narrow" },
                { label: "Medium (600px)", value: "medium" },
                { label: "Wide (800px)", value: "wide" },
                { label: "Extra Wide (1000px)", value: "xwide" },
                { label: "Auto (Natural)", value: "auto" }
              ]
            },
            align: {
              type: "radio",
              label: "Alignment within hero",
              options: [
                { label: "Left", value: "left" },
                { label: "Center", value: "center" }
              ]
            }
          }
        }
      },
      defaultProps: {
        title: "Welcome to Our Community",
        description:
          "Engage with your local government and make your voice heard",
        buttons: [{ label: "Learn More", href: "#", variant: "primary" }],
        buttonAlign: "left",
        backgroundType: "none",
        image: {
          url: "",
          mode: "inline"
        },
        video: {
          url: "",
          poster: "",
          autoplay: true,
          loop: true,
          muted: true,
          alt: ""
        },
        padding: "128px",
        titleTypography: {
          fontSize: "40",
          fontWeight: "700",
          titleColor: "#000000"
        },
        descriptionTypography: {
          fontSize: "18",
          fontWeight: "400",
          descriptionColor: "#000000"
        },
        gradient: {
          enabled: false,
          color: "rgba(255, 255, 255, 1)",
          type: "gradient",
          direction: "to left"
        },
        textOverlay: {
          enabled: false,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          borderRadius: "8",
          shadow: true,
          padding: "1.5rem",
          overlayAlign: "left",
          overlayWidth: "full",
          align: "left"
        }
      },
      render: ({
        title,
        description,
        buttons: configuredButtons,
        buttonAlign,
        backgroundType,
        image,
        video,
        titleTypography,
        descriptionTypography,
        gradient,
        textOverlay
      }) => {
        // Hide signed-out-only buttons (e.g. "Sign in") for signed-in visitors
        // and swap in a name/profile button. See utils/authButtons.js.
        const buttons = applyAuthToButtons(configuredButtons);
        const align = textOverlay?.align || "left";
        const shouldUseImage = backgroundType === "image";
        const shouldUseVideo = backgroundType === "video";

        const isInline = shouldUseImage && image && image.mode === "inline";
        const isBg = shouldUseImage && image && image.mode === "bg";
        const imageUrl = shouldUseImage && image && image.url;
        const videoUrl = shouldUseVideo && video && video.url;
        const videoPoster = shouldUseVideo && video && video.poster;
        const isVideoBg = shouldUseVideo && videoUrl && videoUrl.trim() !== "";

        const titleColor = titleTypography?.titleColor || "#000000";
        const descriptionColor =
          descriptionTypography?.descriptionColor || "#000000";

        const titleSize = titleTypography?.fontSize
          ? `${parseInt(titleTypography.fontSize, 10) / FONT_SIZE_PX_16}rem`
          : "2.5rem";
        const titleWeight = titleTypography?.fontWeight || "700";
        const descSize = descriptionTypography?.fontSize
          ? `${
              parseInt(descriptionTypography.fontSize, 10) / FONT_SIZE_PX_16
            }rem`
          : "1.125rem";
        const descWeight = descriptionTypography?.fontWeight || "400";

        const heroSectionStyle = {
          paddingTop: "clamp(3rem, 8vw, 8rem)",
          paddingBottom: "clamp(3rem, 8vw, 8rem)",
          position: "relative",
          boxSizing: "border-box"
        };

        if (isBg && imageUrl) {
          heroSectionStyle.backgroundImage = `url(${imageUrl})`;
          heroSectionStyle.backgroundSize = "cover";
          heroSectionStyle.backgroundPosition = "center";
        }

        // Container style to match Bootstrap's .container class
        const containerStyle = {
          width: "100%",
          margin: "0 auto",
          paddingLeft: "15px",
          paddingRight: "15px",
          boxSizing: "border-box"
        };

        // Add responsive max-width style tag for Bootstrap container breakpoints
        const containerStyleId = `hero-container-style-${Date.now()}`;
        const containerResponsiveStyle = React.createElement("style", {
          key: containerStyleId,
          dangerouslySetInnerHTML: {
            __html: `
                            .hero-container-${containerStyleId} {
                                width: 100%;
                                margin: 0 auto;
                                padding-left: 15px;
                                padding-right: 15px;
                                box-sizing: border-box;
                            }
                            @media (min-width: 576px) {
                                .hero-container-${containerStyleId} {
                                    max-width: 540px;
                                }
                            }
                            @media (min-width: 768px) {
                                .hero-container-${containerStyleId} {
                                    max-width: 720px;
                                }
                            }
                            @media (min-width: 992px) {
                                .hero-container-${containerStyleId} {
                                    max-width: 960px;
                                }
                            }
                            @media (min-width: 1200px) {
                                .hero-container-${containerStyleId} {
                                    max-width: 1140px;
                                }
                            }
                        `
          }
        });

        const contentWrapperStyle = {
          display: "flex",
          flexDirection: align === "center" ? "column" : "row",
          alignItems: align === "center" ? "center" : "flex-start",
          gap: "clamp(1rem, 4vw, 2rem)",
          position: "relative",
          zIndex: 1,
          width: "100%",
          boxSizing: "border-box"
        };

        const textContentStyle = {
          flex: isInline && imageUrl ? "1" : "none",
          textAlign: align === "center" ? "center" : "left",
          maxWidth: align === "center" ? "min(800px, 100%)" : "100%",
          width: "100%",
          boxSizing: "border-box",
          minWidth: "0"
        };

        const overlayAlign = textOverlay?.overlayAlign || "left";

        function getOverlayPadding() {
          const defaultPadding = textOverlay?.padding || "1.5rem";
          const remMatch = defaultPadding.match(/([\d.]+)rem/);
          if (remMatch) {
            const remValue = parseFloat(remMatch[1]);
            return `clamp(${Math.max(OPACITY_075, remValue * OPACITY_05)}rem, ${
              remValue * OPACITY_075
            }vw, ${remValue}rem)`;
          }
          const pxMatch = defaultPadding.match(/(\d+)px/);
          if (pxMatch) {
            const pxValue = parseInt(pxMatch[1], 10);
            return `clamp(${Math.max(
              FONT_SIZE_PX_12,
              pxValue * OPACITY_05
            )}px, ${pxValue * OPACITY_075}vw, ${pxValue}px)`;
          }
          return defaultPadding;
        }

        const overlayWidth = textOverlay?.overlayWidth || "full";

        const widthMap = {
          full: "100%",
          narrow: "400px",
          medium: "600px",
          wide: "800px",
          xwide: "1000px",
          auto: "auto"
        };

        const overlayBoxStyle = {
          backgroundColor:
            textOverlay.backgroundColor || "rgba(255, 255, 255, 0.9)",
          borderRadius: textOverlay.borderRadius
            ? `${textOverlay.borderRadius}px`
            : "8px",
          padding: getOverlayPadding(),
          boxShadow: textOverlay.shadow
            ? "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
            : "none",
          display: "inline-block",
          width: "100%",
          maxWidth: "100%",
          minWidth: "0",
          boxSizing: "border-box",
          textAlign:
            overlayAlign === "center"
              ? "center"
              : overlayAlign === "right"
              ? "right"
              : "left",
          wordWrap: "break-word",
          overflowWrap: "break-word"
        };

        const overlayWidthClass = `hero-overlay-width-${overlayWidth}`;

        let widthStyleTag = null;
        if (overlayWidth !== "full") {
          const desktopWidth = widthMap[overlayWidth] || "100%";
          const desktopMaxWidth =
            overlayWidth === "auto"
              ? "none"
              : `min(${desktopWidth}, calc(100% - 2rem))`;

          const styleId = `hero-overlay-width-style-${overlayWidth}`;
          widthStyleTag = React.createElement("style", {
            key: styleId,
            dangerouslySetInnerHTML: {
              __html: `
                                @media (min-width: 992px) {
                                    .${overlayWidthClass} {
                                        width: ${
                                          overlayWidth === "auto"
                                            ? "auto"
                                            : desktopWidth
                                        } !important;
                                        max-width: ${desktopMaxWidth} !important;
                                    }
                                }
                            `
            }
          });
        }

        const overlayContainerStyle = {
          width: "100%",
          display: "flex",
          justifyContent:
            overlayAlign === "center"
              ? "center"
              : overlayAlign === "right"
              ? "flex-end"
              : "flex-start",
          boxSizing: "border-box"
        };

        return React.createElement(
          React.Fragment,
          {},
          widthStyleTag,
          containerResponsiveStyle,
          React.createElement(
            "div",
            {
              style: heroSectionStyle
            },
            isVideoBg &&
              React.createElement(
                "video",
                {
                  autoPlay: video.autoplay !== false,
                  loop: video.loop !== false,
                  muted: video.muted !== false,
                  playsInline: true,
                  ...videoA11yProps(video),
                  poster: videoPoster || imageUrl || null,
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 0,
                    pointerEvents: "none"
                  }
                },
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/mp4"
                }),
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/webm"
                }),
                React.createElement("source", {
                  src: videoUrl,
                  type: "video/ogg"
                })
              ),
            gradient &&
              gradient.enabled &&
              (() => {
                const overlayStyle = {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  zIndex: 0
                };

                if (gradient.type === "solid") {
                  overlayStyle.backgroundColor =
                    gradient.color || "rgba(255, 255, 255, 1)";
                } else {
                  const gradientColor =
                    gradient.color || "rgba(255, 255, 255, 1)";
                  const direction = gradient.direction || "to left";

                  const rgbaMatch = gradientColor.match(
                    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
                  );
                  if (rgbaMatch) {
                    const r = rgbaMatch[1];
                    const g = rgbaMatch[2];
                    const b = rgbaMatch[3];
                    const a =
                      rgbaMatch[4] != null ? parseFloat(rgbaMatch[4]) : 1;
                    overlayStyle.background = `linear-gradient(${direction}, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, ${a}) 100%)`;
                  } else {
                    const hex = gradientColor.replace("#", "");
                    const r = parseInt(
                      hex.substr(HEX_COLOR_START_INDEX, HEX_COLOR_R_END_INDEX),
                      HEX_RADIX
                    );
                    const g = parseInt(
                      hex.substr(
                        HEX_COLOR_G_START_INDEX,
                        HEX_COLOR_R_END_INDEX
                      ),
                      HEX_RADIX
                    );
                    const b = parseInt(
                      hex.substr(
                        HEX_COLOR_B_START_INDEX,
                        HEX_COLOR_R_END_INDEX
                      ),
                      HEX_RADIX
                    );
                    overlayStyle.background = `linear-gradient(${direction}, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 1) 100%)`;
                  }
                }

                return React.createElement("div", { style: overlayStyle });
              })(),
            React.createElement(
              "div",
              {
                className: `hero-container-${containerStyleId}`,
                style: containerStyle
              },
              React.createElement(
                "div",
                {
                  style: contentWrapperStyle
                },
                React.createElement(
                  "div",
                  {
                    style: textContentStyle
                  },
                  textOverlay && textOverlay.enabled
                    ? React.createElement(
                        "div",
                        {
                          style: overlayContainerStyle
                        },
                        React.createElement(
                          "div",
                          {
                            className: overlayWidthClass,
                            style: overlayBoxStyle
                          },
                          React.createElement(
                            "h1",
                            {
                              style: {
                                fontSize: titleSize,
                                fontWeight: titleWeight,
                                marginBottom: "1rem",
                                marginTop: 0,
                                color: titleColor,
                                textShadow: "none"
                              }
                            },
                            title
                          ),
                          React.createElement(
                            "p",
                            {
                              style: {
                                fontSize: descSize,
                                fontWeight: descWeight,
                                lineHeight: "1.6",
                                marginBottom:
                                  buttons && buttons.length > 0 ? "2rem" : 0,
                                color: descriptionColor,
                                textShadow: "none"
                              }
                            },
                            description
                          ),
                          buttons &&
                            buttons.length > 0 &&
                            React.createElement(
                              "div",
                              {
                                style: {
                                  display: "flex",
                                  gap: "clamp(0.5rem, 2vw, 1rem)",
                                  flexWrap: "wrap",
                                  justifyContent:
                                    buttonAlign === "center"
                                      ? "center"
                                      : buttonAlign === "right"
                                      ? "flex-end"
                                      : "flex-start",
                                  width: "100%",
                                  boxSizing: "border-box"
                                }
                              },
                              buttons.map((button, idx) => {
                                const btnClasses = [
                                  "btn",
                                  `btn-${button.variant || "primary"}`,
                                  "btn-lg"
                                ];

                                return React.createElement(
                                  "a",
                                  {
                                    key: idx,
                                    href: button.href || "#",
                                    className: btnClasses.join(" "),
                                    role: "button",
                                    style: {
                                      textDecoration: "none"
                                    }
                                  },
                                  button.label || "Button"
                                );
                              })
                            )
                        )
                      )
                    : React.createElement(
                        React.Fragment,
                        {},
                        React.createElement(
                          "h1",
                          {
                            style: {
                              fontSize: titleSize,
                              fontWeight: titleWeight,
                              marginBottom: "1rem",
                              marginTop: 0,
                              color: titleColor,
                              textShadow:
                                (isBg || isVideoBg) &&
                                titleColor === "#ffffff" &&
                                !(gradient && gradient.enabled)
                                  ? "0 2px 4px rgba(0,0,0,0.5)"
                                  : "none"
                            }
                          },
                          title
                        ),
                        React.createElement(
                          "p",
                          {
                            style: {
                              fontSize: descSize,
                              fontWeight: descWeight,
                              lineHeight: "1.6",
                              marginBottom:
                                buttons && buttons.length > 0 ? "2rem" : 0,
                              color: descriptionColor,
                              textShadow:
                                (isBg || isVideoBg) &&
                                descriptionColor === "#ffffff" &&
                                !(gradient && gradient.enabled)
                                  ? "0 1px 2px rgba(0,0,0,0.5)"
                                  : "none"
                            }
                          },
                          description
                        ),
                        buttons &&
                          buttons.length > 0 &&
                          React.createElement(
                            "div",
                            {
                              style: {
                                display: "flex",
                                gap: "clamp(0.5rem, 2vw, 1rem)",
                                flexWrap: "wrap",
                                justifyContent:
                                  buttonAlign === "center"
                                    ? "center"
                                    : buttonAlign === "right"
                                    ? "flex-end"
                                    : "flex-start",
                                width: "100%",
                                boxSizing: "border-box"
                              }
                            },
                            buttons.map((button, idx) => {
                              const btnClasses = [
                                "btn",
                                `btn-${button.variant || "primary"}`,
                                "btn-lg"
                              ];

                              return React.createElement(
                                "a",
                                {
                                  key: idx,
                                  href: button.href || "#",
                                  className: btnClasses.join(" "),
                                  role: "button",
                                  style: {
                                    textDecoration: "none"
                                  }
                                },
                                button.label || "Button"
                              );
                            })
                          )
                      )
                ),
                isInline &&
                  imageUrl &&
                  React.createElement(
                    "div",
                    {
                      style: {
                        flex: "1",
                        minWidth: "clamp(250px, 40vw, 300px)",
                        maxWidth: "100%",
                        boxSizing: "border-box"
                      }
                    },
                    React.createElement("img", {
                      src: imageUrl,
                      alt: title,
                      style: {
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                      }
                    })
                  )
              )
            )
          )
        );
      }
    },
    Stats: {
      label: "Stats Bar",
      fields: {
        hubId: {
          type: "custom",
          label: "# Hub ID (auto-detected)",
          render: ({ field, value, onChange }) => {
            function getAutoHubId() {
              if (window.modelData && window.modelData.hubId) {
                return window.modelData.hubId;
              }
              if (window.modelData && window.modelData.hub) {
                const hub = window.modelData.hub;
                return hub.id || hub.Id || 0;
              }
              return value || 0;
            }
            const autoHubId = getAutoHubId();
            React.useEffect(() => {
              if (autoHubId > 0 && autoHubId !== value) {
                onChange(autoHubId);
              }
            }, [autoHubId]);
            return React.createElement(
              "div",
              { className: "puck-field" },
              React.createElement("label", {}, field.label),
              React.createElement("input", {
                type: "text",
                value: autoHubId > 0 ? autoHubId : "Auto-detecting...",
                readOnly: true,
                disabled: true,
                style: { backgroundColor: "#e9ecef" }
              })
            );
          }
        },
        selectedStats: {
          type: "text",
          label: "Stats (comma-separated: responses,views,subscribers)"
        },
        statColor: {
          type: "custom",
          label: "Stat Color",
          render: ColorPickerField
        }
      },
      defaultProps: {
        hubId: 0,
        selectedStats: "responses,views,subscribers",
        statColor: "#000000"
      },
      render: ({ hubId, selectedStats, statColor }) => {
        const stats = (selectedStats || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const [statsData, setStatsData] = React.useState(null);
        const [loading, setLoading] = React.useState(true);

        React.useEffect(() => {
          if (!hubId || hubId <= 0) {
            setLoading(false);
            return;
          }

          fetch(`/api/hub/${hubId}/stats`)
            .then((res) => res.json())
            .then((data) => {
              setStatsData(data);
              setLoading(false);
            })
            .catch(() => setLoading(false));
        }, [hubId]);

        if (loading) {
          return React.createElement("div", {}, "Loading stats...");
        }

        return React.createElement(
          "div",
          {
            style: {
              display: "flex",
              justifyContent: "space-around",
              padding: "1rem",
              backgroundColor: "#f8f9fa"
            }
          },
          stats.map((stat) =>
            React.createElement(
              "div",
              {
                key: stat,
                style: { textAlign: "center", color: statColor || "#000" }
              },
              React.createElement(
                "div",
                { style: { fontSize: "2rem", fontWeight: "bold" } },
                statsData ? statsData[stat] || 0 : 0
              ),
              React.createElement(
                "div",
                { style: { textTransform: "uppercase", fontSize: "0.875rem" } },
                stat
              )
            )
          )
        );
      }
    },
    Columns: {
      label: "Columns",
      fields: {
        columns: {
          type: "number",
          label: "Number of Columns",
          min: 1,
          max: MAX_COLUMNS
        },
        gap: {
          type: "select",
          label: "Gap Between Columns",
          options: [
            { label: "None", value: "0" },
            { label: "Small (0.5rem)", value: "0.5rem" },
            { label: "Medium (1rem)", value: "1rem" },
            { label: "Large (1.5rem)", value: "1.5rem" },
            { label: "Extra Large (2rem)", value: "2rem" }
          ]
        }
      },
      defaultProps: {
        columns: 2,
        gap: "1rem"
      },
      zones: {
        "column-0": { title: "Column 1" },
        "column-1": { title: "Column 2" },
        "column-2": { title: "Column 3" },
        "column-3": { title: "Column 4" }
      },
      resolveData: (data, { props }) => {
        const columnCount = props.columns || DEFAULT_COLUMN_COUNT;

        // Create zones object with the appropriate number of columns
        const zones = { ...data.zones };

        // Initialize zones for columns that should exist
        for (let i = 0; i < columnCount; i++) {
          const zoneName = `column-${i}`;
          if (!zones[zoneName]) {
            zones[zoneName] = [];
          }
        }

        // Remove zones for columns that no longer exist
        Object.keys(zones).forEach((zoneName) => {
          if (zoneName.startsWith("column-")) {
            const columnIndex = parseInt(zoneName.split("-")[1]);
            if (columnIndex >= columnCount) {
              delete zones[zoneName];
            }
          }
        });

        return {
          ...data,
          zones
        };
      },
      render: ({ columns, gap, puck }) => {
        const { renderDropZone } = puck || {};

        // Ensure columns is a valid number
        const columnCount =
          typeof columns === "number" && columns >= 1 && columns <= MAX_COLUMNS
            ? columns
            : DEFAULT_COLUMN_COUNT;
        const gapValue = gap || "1rem";

        const containerStyle = {
          display: "flex",
          gap: gapValue,
          flexWrap: "wrap",
          width: "100%"
        };

        const columnStyle = {
          flex: `1 1 0`,
          minWidth: "0",
          maxWidth: "100%"
        };

        // Media query style for mobile responsiveness
        const responsiveStyleId = `columns-responsive-${Date.now()}`;
        const responsiveStyle = React.createElement("style", {
          key: responsiveStyleId,
          dangerouslySetInnerHTML: {
            __html: `
              @media (max-width: 768px) {
                .columns-container-${responsiveStyleId} {
                  flex-direction: column !important;
                }
                .columns-container-${responsiveStyleId} > div {
                  flex: 1 1 100% !important;
                  max-width: 100% !important;
                }
              }
            `
          }
        });

        if (!renderDropZone) {
          return React.createElement(
            "div",
            {
              style: {
                padding: "2rem",
                backgroundColor: "#f8f9fa",
                border: "2px dashed #dee2e6",
                borderRadius: `${BORDER_RADIUS_4_PX}px`,
                textAlign: "center",
                color: "#6c757d"
              }
            },
            "Columns component requires drop zones"
          );
        }

        return React.createElement(
          React.Fragment,
          {},
          responsiveStyle,
          React.createElement(
            "div",
            {
              className: `columns-container-${responsiveStyleId}`,
              style: containerStyle
            },
            Array(columnCount)
              .fill(null)
              .map((_, i) =>
                React.createElement(
                  "div",
                  {
                    key: `column-${i}`,
                    style: columnStyle
                  },
                  renderDropZone({
                    zone: `column-${i}`,
                    minHeight: PERCENT_100,
                    disallow: []
                  })
                )
              )
          )
        );
      }
    },
    VerticalSpace: {
      label: "Vertical Space",
      fields: {
        height: {
          type: "number",
          label: "Height (px)",
          min: 0,
          max: MAX_LENGTH_200
        }
      },
      defaultProps: {
        height: MIN_LENGTH_20
      },
      render: ({ height }) => {
        const validHeight =
          typeof height === "number" && !isNaN(height) && height >= 0
            ? height
            : MIN_LENGTH_20;
        return React.createElement("div", {
          style: { height: `${validHeight}px` }
        });
      }
    },
    Divider: {
      label: "Divider",
      fields: {
        style: {
          type: "select",
          label: "Style",
          options: [
            { label: "Solid", value: "solid" },
            { label: "Dashed", value: "dashed" },
            { label: "Dotted", value: "dotted" }
          ]
        }
      },
      defaultProps: {
        style: "solid"
      },
      render: ({ style }) => {
        return React.createElement("hr", {
          style: {
            borderStyle: style,
            borderColor: "#dee2e6",
            margin: "1rem 0"
          }
        });
      }
    },
    Container: createContainerBlock(
      ColorPickerFieldLocal,
      MediaFieldLocal,
      VideoField,
      () => {
        if (window.getPuckHeaderEditorData) {
          return window.getPuckHeaderEditorData();
        }
        return null;
      }
    ),
    TranslateWidget: TranslateWidget,
    WelcomeBlock: createWelcomeBlock(RichTextField)
  }
};

// Store current header editor data
let currentHeaderData = {
  content: [],
  root: { props: {} }
};

// Global functions for Puck header editor
window.initializePuckHeaderEditor = function (
  containerId,
  initialData,
  onSave
) {
  const container = document.getElementById(containerId);
  if (!container) {
    // console.error("Container not found:", containerId);
    return null;
  }

  // Parse initial data
  let data = {
    content: [],
    root: { props: {} }
  };

  if (initialData) {
    if (typeof initialData === "string") {
      try {
        const parsed = JSON.parse(initialData);
        if (parsed && typeof parsed === "object") {
          data = parsed;
        }
      } catch (e) {
        // console.warn("Invalid JSON data, starting with empty editor", e);
      }
    } else if (typeof initialData === "object" && initialData !== null) {
      data = initialData;
    }
  }

  // Ensure data has the required structure
  if (!data.content) {
    data.content = [];
  }
  if (!data.root) {
    data.root = { props: {} };
  }
  if (!data.root.props) {
    data.root.props = {};
  }

  currentHeaderData = data;

  const root = ReactDOM.createRoot(container);

  root.render(
    React.createElement(Puck, {
      config: puckHeaderConfig,
      data: data,
      onPublish: (newData) => {
        currentHeaderData = newData;
        if (onSave) {
          onSave(newData);
        }
      },
      onChange: (newData) => {
        currentHeaderData = newData;
        // Fire a custom event for real-time updates
        document.dispatchEvent(
          new CustomEvent("puck:dataChange", { detail: { data: newData } })
        );
      },
      // Override UI components to hide publish button and header
      overrides: {
        headerActions: () => null, // Hide the publish button
        header: () => null // Hide the entire header
      }
    })
  );

  return {
    getData: () => {
      return currentHeaderData;
    },
    cleanup: () => {
      root.unmount();
      currentHeaderData = {
        content: [],
        root: { props: {} }
      };
    }
  };
};

window.getPuckHeaderEditorData = function () {
  return currentHeaderData;
};

window.cleanupPuckHeaderEditor = function (editorInstance) {
  if (editorInstance && editorInstance.cleanup) {
    editorInstance.cleanup();
  }
};

// Store React roots by container ID to avoid creating multiple roots for the same container
const headerRenderRoots = new Map();

// Render the public hero output into a caller-supplied element
// (may live inside an isolated scan iframe). Returns the React root so the
// caller can unmount it.
window.renderPuckHeaderContentIntoElement = function (element, data) {
  if (!element) {
    return null;
  }
  var root = ReactDOM.createRoot(element);
  root.render(
    React.createElement(Render, {
      config: puckHeaderConfig,
      data: data || { content: [], root: { props: {} } }
    })
  );
  return root;
};

// Render function for public display
window.renderPuckHeaderContent = function (containerId, data) {
  const container = document.getElementById(containerId);
  if (!container) {
    // console.error("Container not found:", containerId);
    return;
  }

  // Check if a root already exists for this container
  let root = headerRenderRoots.get(containerId);

  if (!root) {
    // Create a new root if one doesn't exist
    root = ReactDOM.createRoot(container);
    headerRenderRoots.set(containerId, root);
  }

  // Use root.render() to update the existing root instead of creating a new one
  root.render(
    React.createElement(Render, {
      config: puckHeaderConfig,
      data: data || { content: [], root: { props: {} } }
    })
  );
};

// Cleanup function to unmount and remove a root when container is removed
window.cleanupPuckHeaderRender = function (containerId) {
  const root = headerRenderRoots.get(containerId);
  if (root) {
    try {
      root.unmount();
    } catch (e) {
      // Ignore errors during unmount
    }
    headerRenderRoots.delete(containerId);
  }
};
